import React, { useState } from 'react'
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { getSubscribedSpecificLabCount, getSubsLabDetails } from '../lib/api/Subscription-Service'
import { useQueries } from '@tanstack/react-query'
import Loader from '../components/common/Loader/Loader'
import { ArrowRight } from 'phosphor-react'
import { formatDuration, launchLab } from '../lib/Funcs/LabWorkspace'
import { StatusCircleIcon } from '../lib/assets/icons/SVG-Icons'

const isLoggedIn = localStorage.getItem('access_token') || false

const checkSameId = (localId, counterID) => {
  return localId === counterID
}

const labInfoData = (lab) => ({
  title: lab?.title || 'System Design & Backend Engineering with AWS',
  thumbnail:
    lab?.image ||
    'https://s3.brilliant.com.bd/blog-bucket/thumbnail/547d86ae-bb8c-4c50-9a94-611ca5dbe247.png',
  objectives:
    lab?.learning_objectives && lab?.learning_objectives.length > 0
      ? lab.learning_objectives
      : [
          ' ⁠Foundational knowledge and practical applications of cloud computing, DevOps, automation, and programming languages.',
          '⁠Hands-on experience with industry-standard tools.',
          ' ⁠Building, deploying, and managing applications or systems in cloud environments.',
        ],
  overview:
    lab?.lab_overview ||
    "This lab environment provides you with a fresh Ubuntu 20.04 virtual machine that comes with VS Code pre-installed as your default text editor. You can expose your applications using the load balancer feature by clicking its dedicated icon. When you're done, you can easily end the lab session by clicking the stop exit icon located in the bottom-left of the sidebar.",
})

const SubscriptionLabDetailsPage = () => {
  const navigate = useNavigate()
  const { plan, labGroupID, moduleID, labId } = useParams()
  const { hasAccess } = useOutletContext()

  const [loading, setLoading] = useState(false)

  const isLabRunning = localStorage.getItem('runningLabData') || false
  const isLabLaunching = localStorage.getItem('startingLabData') || false

  const [labQuery, countQuery] = useQueries({
    queries: [
      {
        queryKey: ['specific-lab-info', labId],
        queryFn: () => getSubsLabDetails(labId),
        enabled: !!labId,
      },
      {
        queryKey: ['specific-lab-count', labId],
        queryFn: () => getSubscribedSpecificLabCount(labId),
        enabled: !!isLoggedIn,
      },
    ],
  })

  const { data: lab, isLoading } = labQuery

  const { data: labCountData, isLoading: isCountLoading } = countQuery

  const labInfo = labInfoData(lab)

  const { thumbnail } = labInfo

  const counterID = lab?._id
  const configId = lab?.vm_type || null

  const isConfigSet = configId !== null

  const labData = {
    configId: configId,
    counterID: counterID,
    labGroupID: labGroupID,
    moduleID: moduleID,
    labId: lab?._id,
    duration: parseInt(lab?.duration),
  }

  const localLaunchingLab = JSON.parse(isLabLaunching)
  const localRunningLab = JSON.parse(isLabRunning)

  const isThisLabRunning = checkSameId(localRunningLab?.labId, counterID)

  const isThisLabLaunching = checkSameId(localLaunchingLab.counterID, counterID)

  const handleStartLab = async () => {
    if (isLabLaunching) {
      if (isThisLabLaunching && isThisLabRunning && isLabRunning) {
        return navigate(`/launch/playground/subscribed/${labGroupID}/${moduleID}/${lab._id}`)
      } else if (isThisLabLaunching) return navigate('/subscription/lab-loading')
    }

    return launchLab(navigate, labData, true)
  }

  function checkAccess() {
    if (!hasAccess) return false

    if (plan === 'Free') {
      return true
    }

    if (plan === 'Pro' && hasAccess?.hasPro === true) return true

    if (plan === 'Pro Plus' && hasAccess?.hasProPlus === true) return true
  }

  const handleLabClick = () => {
    setLoading(true)
    if (!isConfigSet) {
      alert('Configuration not set. Contact admin.')
      return
    }

    if (!checkAccess()) {
      setLoading(false)

      return alert('Please upgrade your plan to access this lab.')
    }

    handleStartLab()
  }

  function renderBadge() {
    if (Boolean(isThisLabRunning && isLabRunning)) {
      return <StatusBadge isRunning={true} />
    } else if (Boolean(isThisLabLaunching && isLabLaunching)) {
      return <StatusBadge isRunning={false} />
    }
    return null
  }

  const isButtonDisabled =
    plan === 'Free' ? parseInt(labCountData?.counter) >= parseInt(lab?.count) : false

  return (
    <section className="w-full font-ibm">
      {isLoading ? (
        <Loader />
      ) : (
        <div className=" flex flex-col lg:flex-row justify-between gap-5 md:gap-10 xl:gap-24 py-10 duration-300 ease-in-out">
          {/* Details */}
          <SpecificLabDetailsPart
            labInfo={labInfo}
            loading={loading}
            handleLabClick={handleLabClick}
            renderBadge={renderBadge}
            isButtonDisabled={isButtonDisabled}
          />
          {/* Card */}
          <SpecificLabCardPart
            lab={lab}
            loading={loading}
            labCountData={labCountData}
            thumbnail={thumbnail}
            handleLabClick={handleLabClick}
            isStatLoading={isCountLoading}
            isButtonDisabled={isButtonDisabled}
          />
        </div>
      )}
    </section>
  )
}

const SpecificLabDetailsPart = ({
  labInfo,
  handleLabClick,
  loading,
  renderBadge,
  isButtonDisabled,
}) => {
  const { title, overview, objectives, thumbnail } = labInfo

  return (
    <div className="flex flex-col gap-4 md:gap-6 xl:gap-10 xl:max-w-[60%]">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 lg:gap-4">
        <div className="breadcrumbs py-1 text-sm lg:text-base h-full">
          <ul>
            <li>
              <Link to={'/all-lab-groups'}>Labs</Link>
            </li>
            <li>
              <span className="text-gray-500 text-wrap">{title}</span>
            </li>
          </ul>
        </div>
        {renderBadge()}
      </div>
      {/* Thumbnail */}
      <figure className="w-full relative aspect-video sm:max-w-[70%] lg:hidden block overflow-hidden rounded-lg">
        <img src={thumbnail} alt="lab_thumbnail" className="w-full object-cover rounded-lg" />
      </figure>
      {/* Title */}
      <h1 className="text-xl lg:text-4xl font-semibold">{title}</h1>
      {/* Detailed Section */}
      <div className="flex flex-col gap-4 lg:gap-6">
        {/* Overview */}
        <dd className="flex flex-col gap-4">
          <h4 className="text-blue/70 text-lg lg:text-2xl font-semibold">Overview of This Lab</h4>
          <p className="leading-8 text-sm">{overview}</p>
        </dd>
        {/* Objective List */}
        <dd className="flex flex-col gap-4">
          <h4 className="text-blue/70 text-lg lg:text-2xl font-semibold">What You'll Learn</h4>
          <ul className={`flex flex-col gap-2 lg:gap-4`}>
            {objectives.map((listText, i) => (
              <li key={i} className="flex gap-2 lg:gap-4 text-sm lg:text-base">
                <figure className="size-5 aspect-square">
                  <img src="/tick-square.svg" alt="list" className="size-5 aspect-square" />
                </figure>
                {listText}
              </li>
            ))}
          </ul>
        </dd>
        {/* Action */}
        <div className="hidden xl:block">
          <LabStartButton
            onClick={handleLabClick}
            loading={loading}
            isButtonDisabled={isButtonDisabled}
          />
        </div>
      </div>
    </div>
  )
}

const SpecificLabCardPart = ({
  thumbnail,
  lab,
  labCountData,
  handleLabClick,
  loading,
  isStatLoading,
  isButtonDisabled,
}) => {
  const { plan } = useParams()
  const isFree = plan === 'Free'

  return (
    <div className="w-full lg:w-[56%] lg:max-w-[370px] flex flex-col gap-6 xl:items-end">
      {/* Thumbnail */}
      <figure className="w-full h-44 relative hidden lg:block">
        <img src={thumbnail} alt="lab_thumbnail" className="size-full object-cover rounded-lg" />
      </figure>
      {/* Time */}
      <div className="w-full bg-lab-purple/50 rounded-lg p-2">
        <div className="grid grid-cols-3 justify-between items-start space-x-1">
          <div className="p-1">
            <p className="text-xs sm:text-sm text-gray-600">Total Hour:</p>
            <div className="text-sm sm:text-base font-semibold text-blue">
              {formatDuration(lab.count, parseInt(lab.duration))}
            </div>
          </div>
          <div className="p-1">
            <p className="text-xs sm:text-sm text-gray-600">Lab Count</p>
            {isFree ? (
              <p className="text-sm sm:text-base font-semibold text-blue">
                {isStatLoading ? 0 : labCountData.counter}/{lab.count}
              </p>
            ) : (
              <p className="text-sm sm:text-base font-semibold text-blue">Unlimited</p>
            )}
          </div>
        </div>
      </div>
      {/* Button */}
      <LabStartButton
        onClick={handleLabClick}
        loading={loading}
        isButtonDisabled={isButtonDisabled}
      />
    </div>
  )
}

const LabStartButton = ({ onClick, loading, isButtonDisabled }) => {
  if (isLoggedIn)
    return (
      <button
        onClick={() => onClick()}
        disabled={loading || isButtonDisabled}
        className="size-fit flex gap-2 items-center justify-center px-7 py-2.5 border-0.5 duration-300 text-white rounded-btn bg-blue/90 hover:bg-blue text-sm md:text-base disabled:bg-blue/50">
        <span>{loading ? 'Redirecting' : 'Start'}</span>
        <ArrowRight className="fill-white -rotate-45" />
      </button>
    )
}

const StatusBadge = ({ isRunning = false }) => {
  return (
    <div
      className={`flex items-center gap-1.5 text-2xs backdrop-blur-sm w-fit p-1 rounded font-medium ${
        isRunning ? 'bg-green-100/10 text-green-900' : 'bg-red/10 text-red'
      }`}>
      {isRunning ? 'Running' : 'Launching'}
      <StatusCircleIcon className="size-2 fill-current" />
      <StatusCircleIcon className="size-2 fill-current absolute right-1 animate-ping" />
    </div>
  )
}

export default SubscriptionLabDetailsPage
